import { useEffect, useState } from 'react';
import { api } from '../api';
import type { EventInfo, Question } from '../types';
import { EventClosedPage } from './EventClosedPage';
import { formatEventDate } from '../utils/formatEventDate';
import { formatPhoneInput, isValidRuPhone } from '../utils/phone';

interface Props {
  eventCode: string;
  onSuccess: (lotteryNumber: number) => void;
}

const DEFAULT_PD_TEXT =
  'Я даю согласие на обработку персональных данных в соответствии с политикой клиники Авиценна';

export function SurveyPage({ eventCode, onSuccess }: Props) {
  const [event, setEvent] = useState<EventInfo | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [closedLotteryNumber, setClosedLotteryNumber] = useState<number | undefined>();
  const [pdConsent, setPdConsent] = useState(false);
  const [pdConsentText, setPdConsentText] = useState(DEFAULT_PD_TEXT);

  useEffect(() => {
    async function load() {
      try {
        const [evt, config] = await Promise.all([
          api.getEventByCode(eventCode),
          api.getPublicConfig().catch(() => null),
        ]);

        if (config?.pdConsentText) {
          setPdConsentText(config.pdConsentText);
        }

        const existing = await api.getMyResponse(evt.id);

        if (existing.submitted && existing.lotteryNumber) {
          onSuccess(existing.lotteryNumber);
          return;
        }

        if (!evt.isActive) {
          setEvent(evt);
          setClosedLotteryNumber(existing.lotteryNumber);
          return;
        }

        const qs = await api.getQuestions();
        setEvent(evt);
        setQuestions(qs);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eventCode, onSuccess]);

  const current = questions[step];
  const isLastStep = step === questions.length - 1;
  const progress = questions.length ? ((step + 1) / questions.length) * 100 : 0;

  function setAnswer(value: string) {
    if (!current) return;
    setAnswers((prev) => ({ ...prev, [String(current.id)]: value }));
  }

  function handlePhoneChange(raw: string) {
    setAnswer(formatPhoneInput(raw));
  }

  function validateCurrentStep(): string | null {
    if (!current) return 'Анкета недоступна';

    const value = answers[String(current.id)]?.trim();
    if (current.isRequired && !value) {
      return 'Заполните это поле';
    }

    if (current.type === 'phone' && value && !isValidRuPhone(value)) {
      return 'Укажите корректный номер телефона в формате +7 (XXX) XXX-XX-XX';
    }

    return null;
  }

  function canProceed() {
    if (!current) return false;
    if (validateCurrentStep()) return false;
    if (isLastStep && !pdConsent) return false;
    return true;
  }

  async function handleSubmit() {
    if (!event) return;

    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!pdConsent) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await api.submitResponse(event.code, answers, pdConsent);
      onSuccess(result.lotteryNumber);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка отправки');
    } finally {
      setSubmitting(false);
    }
  }

  function handleNext() {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    if (step < questions.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    handleSubmit();
  }

  if (loading) {
    return <div className="loading">Загрузка анкеты…</div>;
  }

  if (event && !event.isActive) {
    return <EventClosedPage eventName={event.name} lotteryNumber={closedLotteryNumber} />;
  }

  if (!event || !current) {
    return <div className="error">{error || 'Анкета недоступна'}</div>;
  }

  return (
    <div>
      <div className="header">
        <div className="logo">Клиника Авиценна</div>
        <h1 className="title">{event.name}</h1>
        <p className="subtitle">
          {formatEventDate(event.eventDate)
            ? `Дата: ${formatEventDate(event.eventDate)} · `
            : ''}
          Заполните короткую анкету и получите номер для участия в лотерее
        </p>
      </div>

      <div className="progress">
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <h2>
          Вопрос {step + 1} из {questions.length}
        </h2>

        <div className="field">
          <label>
            {current.text}
            {!current.isRequired && ' (необязательно)'}
          </label>

          {current.type === 'single_choice' ? (
            <div className="options">
              {current.options.map((opt) => (
                <label
                  key={opt}
                  className={`option ${answers[String(current.id)] === opt ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`q-${current.id}`}
                    checked={answers[String(current.id)] === opt}
                    onChange={() => setAnswer(opt)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          ) : (
            <input
              type={current.type === 'phone' ? 'tel' : 'text'}
              inputMode={current.type === 'phone' ? 'tel' : 'text'}
              placeholder={current.type === 'phone' ? '+7 (___) ___-__-__' : 'Ваш ответ'}
              value={answers[String(current.id)] || ''}
              onChange={(e) =>
                current.type === 'phone'
                  ? handlePhoneChange(e.target.value)
                  : setAnswer(e.target.value)
              }
            />
          )}
        </div>

        {isLastStep && (
          <label className="checkbox-row pd-consent">
            <input
              type="checkbox"
              checked={pdConsent}
              onChange={(e) => setPdConsent(e.target.checked)}
            />
            <span>{pdConsentText}</span>
          </label>
        )}

        <div className="btn-row">
          {step > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => setStep((s) => s - 1)}>
              Назад
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canProceed() || submitting}
            onClick={handleNext}
          >
            {step < questions.length - 1 ? 'Далее' : submitting ? 'Отправка…' : 'Получить номер'}
          </button>
        </div>
      </div>
    </div>
  );
}
